import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { errMsg, eur, fmtDate, num, useApi } from "@/lib/api";
import { StateBadge, StatusBadge } from "@/components/club/StatusBadge";
import { Btn, Table, Td, downloadCsv } from "./AdminUi";

const ROLE_LABEL = { admin: "Admin", partner: "Partner", customer: "Cliente" };

export function AdminCustomers() {
  const api = useApi();
  const [rows, setRows] = useState([]);
  useEffect(() => { api("get", "/admin/customers").then(setRows).catch(() => {}); }, [api]);
  return (
    <Table testid="admin-customers" empty={!rows.length} head={["Cliente", "Ruolo", "Status", "Saldo", "Punti anno", "Scadenza status"]}>
      {rows.map((c) => (
        <tr key={c.user_id} data-testid={`admin-customer-${c.user_id}`}>
          <Td><p className="text-white">{c.full_name || "—"}</p><p className="text-xs text-slate-500">{c.email} · {c.phone}</p></Td>
          <Td>{ROLE_LABEL[c.role]}</Td>
          <Td><StatusBadge status={c.status} small /></Td>
          <Td>{num(c.balance)} pt <span className="text-xs text-slate-500">({eur(c.balance_value)})</span></Td>
          <Td>{num(c.earned_this_year)}</Td>
          <Td>{c.valid_until ? fmtDate(c.valid_until) : "—"}</Td>
        </tr>
      ))}
    </Table>
  );
}

export function AdminDeclarations() {
  const api = useApi();
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => setRows(await api("get", "/admin/declarations")), [api]);
  useEffect(() => { load().catch(() => {}); }, [load]);
  const review = async (d, approve) => {
    try {
      const r = await api("post", `/admin/declarations/${d.id}/review`, { approve, note: approve ? "" : "Acquisto non verificato" });
      toast.success(approve ? `Accreditati ${r.points} punti` : "Dichiarazione rifiutata");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Table testid="admin-declarations" empty={!rows.length} head={["Cliente", "Acquisto", "Importo", "Stato", "Azioni"]}>
      {rows.map((d) => (
        <tr key={d.id} data-testid={`admin-declaration-${d.id}`}>
          <Td>{d.customer_email}<p className="text-xs text-slate-500">{fmtDate(d.created_at)}</p></Td>
          <Td><p className="text-white">{d.product_name}</p><p className="text-xs text-slate-500">{d.shop} · ordine {d.order_number}</p></Td>
          <Td>{eur(d.amount)}</Td>
          <Td><StateBadge status={d.status} />{d.points_awarded > 0 && <p className="text-xs text-amber-300">+{d.points_awarded} pt</p>}</Td>
          <Td>{d.status === "pending" && <div className="flex gap-2"><Btn variant="ok" testid={`approve-declaration-${d.id}`} onClick={() => review(d, true)}>Verifica e accredita</Btn><Btn variant="warn" testid={`reject-declaration-${d.id}`} onClick={() => review(d, false)}>Rifiuta</Btn></div>}</Td>
        </tr>
      ))}
    </Table>
  );
}

export function AdminReport() {
  const api = useApi();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => setRows(await api("get", "/admin/report", null, { month })), [api, month]);
  useEffect(() => { load().catch(() => {}); }, [load]);
  const toggle = async (r, paid) => {
    await api("post", "/admin/report/payout", { partner_id: r.partner_id, month, paid });
    load();
  };
  const exportCsv = () => downloadCsv(`report-partner-${month}.csv`, [["Mese", "Azienda", "Ordini", "Totale incassato", "Di cui spedizioni", "IBAN", "Riversato"],
    ...rows.map((r) => [month, r.company_name, r.orders, r.total.toFixed(2), r.shipping.toFixed(2), r.iban, r.paid ? "Sì" : "No"])]);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div><Label className="text-xs text-slate-400">Mese</Label><Input data-testid="report-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1 w-48 bg-[#12141C]" /></div>
        <Btn testid="report-export" onClick={exportCsv}>Esporta CSV</Btn>
      </div>
      <Table testid="admin-report" empty={!rows.length} head={["Azienda", "Ordini", "Totale incassato", "Di cui spedizioni", "IBAN", "Riversato"]}>
        {rows.map((r) => (
          <tr key={r.partner_id} data-testid={`report-row-${r.partner_id}`}>
            <Td className="text-white">{r.company_name}</Td>
            <Td>{r.orders}</Td>
            <Td className="font-semibold text-white">{eur(r.total)}</Td>
            <Td>{eur(r.shipping)}</Td>
            <Td className="font-mono text-xs">{r.iban}</Td>
            <Td><Switch data-testid={`payout-toggle-${r.partner_id}`} checked={r.paid} onCheckedChange={(v) => toggle(r, v)} /></Td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

export function AdminUsers() {
  const api = useApi();
  const [data, setData] = useState({ roles: [], users: [] });
  const load = useCallback(async () => setData(await api("get", "/users")), [api]);
  useEffect(() => { load().catch(() => {}); }, [load]);
  const setRole = async (u, role) => {
    try {
      await api("patch", `/users/${u.user_id}/role`, { role });
      toast.success("Ruolo aggiornato");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Table testid="admin-users" empty={!data.users.length} head={["Utente", "Ruolo"]}>
      {data.users.map((u) => (
        <tr key={u.user_id} data-testid={`admin-user-${u.user_id}`}>
          <Td><p className="text-white">{u.full_name || "—"}</p><p className="text-xs text-slate-500">{u.email}</p></Td>
          <Td>
            <Select value={u.role} onValueChange={(v) => setRole(u, v)}>
              <SelectTrigger data-testid={`role-select-${u.user_id}`} className="h-8 w-40 bg-[#1C1F2C] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{data.roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r] || r}</SelectItem>)}</SelectContent>
            </Select>
          </Td>
        </tr>
      ))}
    </Table>
  );
}

export function AdminEmails() {
  const api = useApi();
  const [rows, setRows] = useState([]);
  useEffect(() => { api("get", "/admin/emails").then(setRows).catch(() => {}); }, [api]);
  const KIND = { ordine: "Ordine ricevuto", bonifico: "Bonifico ricevuto", spedito: "Spedito", pronto_ritiro: "Pronto ritiro", calo_prezzo: "Calo prezzo" };
  return (
    <Table testid="admin-emails" empty={!rows.length} head={["Data", "Destinatario", "Tipo", "Oggetto", "Esito"]}>
      {rows.map((e) => (
        <tr key={e.id}>
          <Td className="whitespace-nowrap text-xs">{new Date(e.created_at).toLocaleString("it-IT")}</Td>
          <Td className="text-xs">{e.to || "—"}</Td>
          <Td className="text-xs">{KIND[e.kind] || e.kind}</Td>
          <Td className="max-w-md text-xs text-slate-300">{e.subject}</Td>
          <Td><span className={`rounded-full border px-2 py-0.5 text-xs ${e.status === "inviata" ? "border-emerald-500/30 text-emerald-300" : "border-red-500/30 text-red-300"}`}>{e.status === "inviata" ? "Inviata" : e.status === "email_mancante" ? "Email mancante" : "Errore"}</span></Td>
        </tr>
      ))}
    </Table>
  );
}

export function AdminSms() {
  const api = useApi();
  const [rows, setRows] = useState([]);
  useEffect(() => { api("get", "/admin/sms").then(setRows).catch(() => {}); }, [api]);
  const LABEL = { inviato: "approved", non_configurato: "pending", numero_mancante: "suspended", errore: "rejected" };
  const TXT = { inviato: "Inviato", non_configurato: "Twilio non configurato", numero_mancante: "Numero mancante", errore: "Errore" };
  return (
    <Table testid="admin-sms" empty={!rows.length} head={["Data", "Destinatario", "Messaggio", "Esito"]}>
      {rows.map((s) => (
        <tr key={s.id}>
          <Td className="whitespace-nowrap text-xs">{new Date(s.created_at).toLocaleString("it-IT")}</Td>
          <Td>{s.audience === "admin" ? "Admin" : "Cliente"}<p className="text-xs text-slate-500">{s.to || "—"}</p></Td>
          <Td className="max-w-md text-xs text-slate-300">{s.body}</Td>
          <Td><span className={`rounded-full border px-2 py-0.5 text-xs ${{ approved: "border-emerald-500/30 text-emerald-300", pending: "border-amber-500/30 text-amber-300", suspended: "border-zinc-500/30 text-zinc-300", rejected: "border-red-500/30 text-red-300" }[LABEL[s.status]]}`}>{TXT[s.status] || s.status}</span></Td>
        </tr>
      ))}
    </Table>
  );
}
